import {Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {
  AcquirerMerchantId,
  Campaign,
  ChipTone,
  CRM,
  CRMTypeId,
  CustomDomain,
  ListOptionRule,
  MerchantProfile,
  MerchantProfilePayload,
  MerchantProfileResolve,
  MerchantProfileResolveFormValue,
  MerchantProfileResolvePayload,
  MultiSelectOption,
  Pager,
  ProductChip,
  ResolveBusinessType,
  ResolveBusinessTypeLabels,
  ResolveIndustryCodeOptions,
  ResolveSyncStatus,
  SelectOption,
} from '../_models';
import {ActivatedRoute, Router} from '@angular/router';
import {CrudSavePopupComponent} from '../_directives';
import {Location} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {
  AlertService,
  CampaignDomainService,
  CampaignService,
  CRMService,
  MerchantProfileService,
} from '../_services';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {CustomValidators} from '../_helpers';
import {config} from '../../config/config';
import {ResolveTestOrderComponent} from './resolve-test-order.component';

@Component({
  selector: 'app-merchant-profiles-resolve',
  templateUrl: './merchant-profiles-resolve.component.html',
  styleUrls: [
    './merchant-profiles-edit.component.scss',
    '../checkouts/checkout-profiles/profiles-styles.component.scss',
  ],
})
export class MerchantProfilesResolveComponent extends CrudSavePopupComponent implements OnInit, OnChanges {
  @Input() parentDirty = false;
  @ViewChild(ResolveTestOrderComponent, { static: false }) testOrder: ResolveTestOrderComponent;
  chipTone = ChipTone;
  descriptorStatus: ProductChip = { label: 'Pending', tone: ChipTone.Pending };
  // One label holding code and name, so typing either finds the option
  industryCodeOptions: SelectOption[] = ResolveIndustryCodeOptions.map((option: SelectOption) => {
    return { value: option.value, label: option.value + ' - ' + option.label };
  });
  listOptionRule = ListOptionRule;
  businessTypes = [
    ResolveBusinessType.SoleProprietorship,
    ResolveBusinessType.BusinessCorporation,
    ResolveBusinessType.Other,
  ];
  businessTypeLabels = ResolveBusinessTypeLabels;
  availableCrms: MultiSelectOption[] = [];
  multiSelectSettings = {
    enableCheckAll: false,
  };
  merchantProfile: MerchantProfile = null;
  campaign: Campaign;
  customDomains: CustomDomain[] = [];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected merchantProfileService: MerchantProfileService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected campaignService: CampaignService,
    protected domainService: CampaignDomainService,
    protected crmService: CRMService,
    protected modalService: NgxSmartModalService
  ) {
    super(router, location, route, merchantProfileService, alertService);
    this.isPartial = true;
    this.objectName = 'Resolve details';
  }

  ngOnInit() {
    this.campaign = this.campaignService.getCurrentCampaign();
    this.form = this.formBuilder.group({
      id: [null],
      descriptors: this.formBuilder.array([this.buildDescriptorControl(null)]),
      acquirer_merchant_ids: this.formBuilder.array([]),
      industry_code: [null, [Validators.required, CustomValidators.oneOf(ResolveIndustryCodeOptions.map((option) => option.value))]],
      business_type: [null, [Validators.required]],
      domain: [null],
      crms: [[]],
      is_enabled: [false],
    });

    super.ngOnInit();

    this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      if (this.form.dirty) {
        this.canDeactivateService.setFormDirty(true);
      }
    });

    this.canDeactivateService.getSaveModalEventObs().pipe(takeUntil(this._destroy$)).subscribe((data) => {
      if (data && data.event === 'save' && this.form.dirty) {
        this.returnUrl = this.canDeactivateService.getCurrentRouteTo();
        this.onSubmit();

        if (!this.form.valid) {
          this.returnUrl = null;
        }
      }
    });

    this.loadDomains();
    this.loadCrms();
  }

  ngOnChanges(changes?: SimpleChanges) {
    if (changes && changes.inputData) {
      super.ngOnChanges();
    }
  }

  // Same limits as the API: a row of only spaces would be dropped on save, and each descriptor is max 50 characters
  protected buildDescriptorControl(value: string): FormControl {
    return this.formBuilder.control(value, [Validators.required, Validators.pattern(/\S/), Validators.maxLength(50)]);
  }

  // Same limits as the API: both are required, a 6-8 character BIN and a card acceptor ID of at most 15
  protected buildAcquirerMerchantIdGroup(pair: AcquirerMerchantId): FormGroup {
    return this.formBuilder.group({
      acquirer_bin: [pair ? pair.acquirer_bin : null, [Validators.required, Validators.minLength(6), Validators.maxLength(8)]],
      card_acceptor_id: [pair ? pair.card_acceptor_id : null, [Validators.required, Validators.maxLength(15)]],
    });
  }

  // The input is the whole profile, so its resolve block replaces what the base patched in from the profile's own fields
  protected onInputDataChanged() {
    this.merchantProfile = this.inputData;

    if (this.merchantProfile) {
      const resolve = this.merchantProfile.resolve;

      this.setDescriptorRows(resolve ? resolve.descriptors : []);
      this.setAcquirerMerchantIdRows(resolve ? resolve.acquirer_merchant_ids : []);
      this.form.patchValue(this.toFormValue(resolve));
      this.applySelectedCrms();
    }
  }

  // patchValue can't take a null group, so a profile without Resolve gets the empty shape
  protected toFormValue(resolve: MerchantProfileResolve): MerchantProfileResolveFormValue {
    let value: MerchantProfileResolveFormValue = {
      id: null,
      descriptors: [null],
      acquirer_merchant_ids: [],
      industry_code: null,
      business_type: null,
      domain: null,
      crms: [],
      is_enabled: false,
    };

    if (resolve) {
      value = {
        id: resolve.id,
        descriptors: this.withOneDescriptor(resolve.descriptors),
        acquirer_merchant_ids: resolve.acquirer_merchant_ids || [],
        industry_code: resolve.industry_code,
        business_type: resolve.business_type,
        domain: resolve.domain,
        crms: [],
        is_enabled: resolve.is_enabled,
      };
    }

    return value;
  }

  protected getFormData(): MerchantProfilePayload {
    const value: MerchantProfileResolveFormValue = this.form.value;
    const resolve: MerchantProfileResolvePayload = {
      ...value,
      descriptors: this.cleanDescriptors(value.descriptors),
      acquirer_merchant_ids: this.cleanAcquirerMerchantIds(value.acquirer_merchant_ids),
      crms: (value.crms || []).map((option) => option.id),
    };

    if (!this.form.get('crms').dirty) {
      delete resolve.crms;
    }

    if (!resolve.id) {
      delete resolve.id;
    }

    return { resolve };
  }

  // The API nests field errors under the resolve block, while this form holds the Resolve fields at its top level
  protected handleSubmitError(error: HttpErrorResponse, keepAfterNavigationChange: boolean = false, showAlert: boolean = false) {
    let fieldError = error;

    if (error && error.status === 400 && error.error && error.error.resolve) {
      fieldError = new HttpErrorResponse({ error: error.error.resolve, status: error.status });
    }

    this.returnUrl = null;
    super.handleSubmitError(fieldError, keepAfterNavigationChange, showAlert);
  }

  protected onSaveComplete(data: MerchantProfile) {
    const returnUrl = this.returnUrl;

    this.returnUrl = null;
    super.onSaveComplete(data);

    if (this.parentDirty) {
      this.canDeactivateService.setFormDirty(true);
    } else if (returnUrl) {
      this.router.navigate([returnUrl]);
    }
  }

  get hasSavedResolve(): boolean {
    return !!(this.merchantProfile && this.merchantProfile.resolve);
  }

  get descriptorControls(): FormArray {
    return this.form.get('descriptors') as FormArray;
  }

  get acquirerMerchantIdControls(): FormArray {
    return this.form.get('acquirer_merchant_ids') as FormArray;
  }

  getResolveChip(): ProductChip {
    let chip: ProductChip = { label: 'Not Configured', tone: ChipTone.None };

    if (this.hasSavedResolve) {
      const syncStatus = this.merchantProfile.resolve.sync_status;

      if (syncStatus === ResolveSyncStatus.Inactive) {
        chip = { label: 'Inactive', tone: ChipTone.Disabled };
      } else if (syncStatus === ResolveSyncStatus.Live) {
        chip = { label: 'Verified', tone: ChipTone.Verified };
      } else {
        chip = { label: 'Pending Verification', tone: ChipTone.Pending };
      }
    }

    return chip;
  }

  openTestOrder() {
    this.modalService.getModal('resolveTestOrderDialog').open();
  }

  onTestOrderProductSelected(productIds: string[]) {
    this.modalService.getModal('resolveTestOrderProductsDialog').close();

    if (this.testOrder) {
      this.testOrder.onProductSelected(productIds);
    }
  }

  descriptorPath(index: number): string {
    return 'descriptors.' + index;
  }

  // FormArray push and removeAt don't mark the array dirty on their own
  addDescriptor() {
    this.descriptorControls.push(this.buildDescriptorControl(null));
    this.descriptorControls.markAsDirty();
  }

  removeDescriptor(index: number) {
    this.descriptorControls.removeAt(index);
    this.descriptorControls.markAsDirty();
  }

  // A server error on the row wins over the client checks
  getDescriptorError(index: number): string {
    const errors = this.descriptorControls.at(index).errors || {};
    let message = 'Descriptor is required';

    if (errors.string) {
      message = errors.string;
    } else if (errors.maxlength) {
      message = 'Descriptor must be at most ' + errors.maxlength.requiredLength + ' characters';
    }

    return message;
  }

  acquirerMerchantIdPath(index: number, field: string): string {
    return 'acquirer_merchant_ids.' + index + '.' + field;
  }

  // FormArray push and removeAt don't mark the array dirty on their own
  addAcquirerMerchantId() {
    this.acquirerMerchantIdControls.push(this.buildAcquirerMerchantIdGroup(null));
    this.acquirerMerchantIdControls.markAsDirty();
  }

  removeAcquirerMerchantId(index: number) {
    this.acquirerMerchantIdControls.removeAt(index);
    this.acquirerMerchantIdControls.markAsDirty();
  }

  hasDescriptorValue(index: number): boolean {
    return this.cleanDescriptors([this.descriptorControls.at(index).value]).length > 0;
  }

  // One input row per stored descriptor, and a single empty row when there are none
  protected setDescriptorRows(descriptors: string[]) {
    this.descriptorControls.clear();
    this.withOneDescriptor(descriptors).forEach((descriptor) => {
      this.descriptorControls.push(this.buildDescriptorControl(descriptor));
    });
  }

  protected setAcquirerMerchantIdRows(pairs: AcquirerMerchantId[]) {
    this.acquirerMerchantIdControls.clear();
    (pairs || []).forEach((pair) => {
      this.acquirerMerchantIdControls.push(this.buildAcquirerMerchantIdGroup(pair));
    });
  }

  // The API rejects a blank string, so an emptied half goes out as null
  protected cleanAcquirerMerchantIds(pairs: AcquirerMerchantId[]): AcquirerMerchantId[] {
    const clean = (value: string) => (value && String(value).trim() ? String(value).trim() : null);

    return (pairs || []).map((pair) => {
      return { acquirer_bin: clean(pair.acquirer_bin), card_acceptor_id: clean(pair.card_acceptor_id) };
    });
  }

  protected cleanDescriptors(descriptors: string[]): string[] {
    return (descriptors || [])
      .map((descriptor) => (descriptor ? String(descriptor).trim() : ''))
      .filter((descriptor) => descriptor.length > 0);
  }

  protected withOneDescriptor(descriptors: string[]): string[] {
    return descriptors && descriptors.length > 0 ? descriptors : [null];
  }

  protected loadCrms() {
    this.crmService.list({ page: 1, page_size: config.maxPageSize, 'type!': CRMTypeId.Test })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: Pager) => {
          const crms: CRM[] = data.results || [];

          this.availableCrms = crms.map((crm) => {
            return { id: Number(crm.id), text: crm.display_name ? crm.display_name : crm.name };
          });
          this.applySelectedCrms();
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  // Needs both the profile and the CRM list; whichever loads last fills the dropdown
  protected applySelectedCrms() {
    if (this.hasSavedResolve && this.availableCrms.length > 0) {
      const ids = (this.merchantProfile.resolve.crms || []).map((id) => Number(id));
      const control = this.form.get('crms');

      control.patchValue(this.availableCrms.filter((crm) => ids.indexOf(crm.id) > -1));
      control.markAsPristine();
    }
  }

  protected loadDomains() {
    if (this.campaign) {
      this.domainService.list({ campaign: this.campaign.id })
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (domains: CustomDomain[]) => {
            this.customDomains = domains;
          },
          (error) => {
            this.handleError(error);
          }
        );
    }
  }
}
